import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Gauge, Trophy, X } from "lucide-react";
import { api, useFetch } from "../lib/api";
import { EmptyState } from "../components/atoms";
import { getDriverInitials, getTeamWallVisual } from "./teamsWallConfig";
import "./teams.css";

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

const TRACK_PATH =
    "M650 0 C790 250 740 500 580 625 C430 742 222 694 185 930 C152 1138 313 1256 520 1232 C715 1210 876 1267 822 1516 C764 1784 456 1620 320 1742 C176 1872 170 2168 376 2190 C548 2208 610 2074 790 2222 C951 2356 856 2507 686 2588 C542 2658 769 2854 562 2958 C432 3024 208 2900 154 3134 C120 3276 140 3508 188 3800";

const CARD_LAYOUT = [
    "left",
    "left",
    "right",
    "right",
    "left",
    "right",
    "right",
    "left",
    "left",
    "right",
    "right",
];

const TEAM_CARD_REFERENCE = {
    mercedes: { founded: "2010", base: "Brackley, United Kingdom" },
    ferrari: { founded: "1950", base: "Maranello, Italy" },
    mclaren: { founded: "1963", base: "Woking, United Kingdom" },
    red_bull: { founded: "2005", base: "Milton Keynes, United Kingdom" },
    alpine: { founded: "2021", base: "Enstone, United Kingdom" },
    rb: { founded: "2024", base: "Faenza, Italy" },
    haas: { founded: "2016", base: "Kannapolis, United States" },
    williams: { founded: "1977", base: "Grove, United Kingdom" },
    audi: { founded: "2026", base: "Hinwil, Switzerland" },
    cadillac: { founded: "2026", base: "Fishers, United States" },
    aston_martin: { founded: "2021", base: "Silverstone, United Kingdom" },
};

const GENERIC_BASE_VALUES = new Set(["american", "austrian", "british", "french", "german", "italian"]);

function getTeamReference(team) {
    const fallback = TEAM_CARD_REFERENCE[team?.id] || TEAM_CARD_REFERENCE[team?.slug] || {};
    const rawBase = team?.base_location || team?.headquarters || team?.factory_location || team?.base;
    const base = rawBase && !GENERIC_BASE_VALUES.has(String(rawBase).toLowerCase()) ? rawBase : fallback.base;
    return {
        founded: team?.founded_year || team?.founded || team?.year_founded || team?.established || fallback.founded || "—",
        base: base || "Base not published",
    };
}

function isHistoricalTeam(team) {
    return String(team?.active_status || team?.status || "").toLowerCase() === "legendary";
}

function buildDriverEntries(team, driversById) {
    const teamDriverIds = Array.isArray(team.driver_ids) && team.driver_ids.length ? team.driver_ids : team.drivers;
    const ids = Array.isArray(teamDriverIds) ? teamDriverIds.slice(0, 2) : [];
    const resolved = ids
        .map((id) => driversById[id])
        .filter(Boolean)
        .map((driver) => ({
            id: driver.id,
            name: `${driver.first_name} ${driver.last_name}`,
            code: driver.abbreviation || getDriverInitials(`${driver.first_name} ${driver.last_name}`),
            number: driver.number,
            points: driver.points,
            wins: driver.wins,
            podiums: driver.podiums,
            position: driver.position || driver.standing || driver.current_season_position,
        }));

    if (resolved.length) return resolved;

    return ids.map((id) => ({
        id,
        name: String(id || "")
            .split("-")
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" "),
        code: getDriverInitials(id),
        number: null,
        points: null,
        wins: null,
        podiums: null,
        position: null,
    }));
}

function deriveTeamMetrics(team, drivers) {
    const driverPoints = drivers.reduce((sum, driver) => sum + Number(driver.points || 0), 0);
    const driverWins = drivers.reduce((sum, driver) => sum + Number(driver.wins || 0), 0);
    const driverPodiums = drivers.reduce((sum, driver) => sum + Number(driver.podiums || 0), 0);
    const position = Number(team.position || team.standing || 10);
    const pressureIndex = Math.max(100 - position * 8, 12);
    const heritage = Number(team.championships || 0) * 12 + Number(team.wins || 0) * 0.2;

    return {
        driverPoints,
        driverWins,
        driverPodiums,
        pressureIndex: Math.round(pressureIndex),
        heritageIndex: Math.round(heritage),
    };
}

function buildTeamCards(teams, drivers) {
    const driversById = Object.fromEntries((drivers || []).map((driver) => [driver.id, driver]));
    return [...(teams || [])]
        .filter((team) => ["active", "legendary"].includes(String(team.active_status || team.status || "active").toLowerCase()))
        .map((team) => {
            const teamDrivers = buildDriverEntries(team, driversById);
            return {
                ...team,
                visual: getTeamWallVisual(team),
                reference: getTeamReference(team),
                drivers: teamDrivers,
                metrics: deriveTeamMetrics(team, teamDrivers),
            };
        });
}

export default function Teams() {
    const reducedMotion = useReducedMotion();
    const teamsResponse = useFetch(() => api.teamsAll(), []);
    const driversResponse = useFetch(() => api.driversActive(), []);
    const pageRef = useRef(null);
    const frameRef = useRef(0);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [selectedTeam, setSelectedTeam] = useState(null);

    const teams = useMemo(
        () => buildTeamCards(teamsResponse.data?.teams, driversResponse.data?.drivers),
        [teamsResponse.data?.teams, driversResponse.data?.drivers]
    );

    const loading = teamsResponse.loading || driversResponse.loading;
    const pageError = teamsResponse.error;

    const syncScrollProgress = useCallback(() => {
        if (!pageRef.current) return;
        const rect = pageRef.current.getBoundingClientRect();
        const scrollRange = Math.max(rect.height - window.innerHeight, 1);
        setScrollProgress(clamp(-rect.top / scrollRange, 0, 1));
    }, []);

    useEffect(() => {
        syncScrollProgress();

        const schedule = () => {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = requestAnimationFrame(syncScrollProgress);
        };

        window.addEventListener("scroll", schedule, { passive: true });
        window.addEventListener("resize", schedule);
        return () => {
            cancelAnimationFrame(frameRef.current);
            window.removeEventListener("scroll", schedule);
            window.removeEventListener("resize", schedule);
        };
    }, [syncScrollProgress]);

    useEffect(() => {
        if (!selectedTeam) return;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setSelectedTeam(null);
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [selectedTeam]);

    if (loading) {
        return (
            <div className="mx-auto max-w-6xl px-4 py-24" data-testid="teams-page">
                <div className="shimmer h-[520px] w-full rounded-md" />
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-testid="teams-page">
                <EmptyState title="Teams unavailable" icon={Trophy} />
            </div>
        );
    }

    return (
        <div ref={pageRef} className="teams-road-page" data-testid="teams-page">
            <div className="teams-road-bg" />
            <TeamsRoadTrack scrollProgress={scrollProgress} reducedMotion={reducedMotion} />

            <header className="teams-road-header">
                <p className="label-eyebrow text-f1-red">Constructor field</p>
                <h1>F1 Teams</h1>
                <p>Active constructors fighting for the title, plus the teams that shaped the sport.</p>
            </header>

            <section className="teams-road-list" aria-label="F1 teams">
                {teams.map((team, index) => {
                    const startsHistoricalSection = isHistoricalTeam(team) && !isHistoricalTeam(teams[index - 1]);

                    return (
                        <React.Fragment key={team.id}>
                            {startsHistoricalSection ? (
                                <div className="teams-road-history-break">
                                    <span>Historical Teams Onwards</span>
                                    <hr />
                                </div>
                            ) : null}
                            <RoadTeamCard
                                team={team}
                                side={CARD_LAYOUT[index % CARD_LAYOUT.length]}
                                index={index}
                                onOpen={() => setSelectedTeam(team)}
                            />
                        </React.Fragment>
                    );
                })}
            </section>

            <AnimatePresence>
                {selectedTeam ? (
                    <TeamGarageModal team={selectedTeam} onClose={() => setSelectedTeam(null)} />
                ) : null}
            </AnimatePresence>
        </div>
    );
}

function TeamsRoadTrack({ scrollProgress, reducedMotion }) {
    const trackRef = useRef(null);
    const pathRef = useRef(null);
    const [carPosition, setCarPosition] = useState({ x: 65, y: 0, angle: 180 });

    useEffect(() => {
        const path = pathRef.current;
        const track = trackRef.current;
        if (!path || !track) return;

        const length = path.getTotalLength();
        const progress = reducedMotion ? 0.08 : clamp(scrollProgress, 0.006, 0.994);
        const currentDistance = length * progress;
        const tangentSample = Math.max(length * 0.0025, 5);
        const previous = path.getPointAtLength(Math.max(currentDistance - tangentSample, 0));
        const current = path.getPointAtLength(currentDistance);
        const next = path.getPointAtLength(Math.min(currentDistance + tangentSample, length));
        const trackRect = track.getBoundingClientRect();
        const dx = (next.x - previous.x) * (trackRect.width / 1000);
        const dy = (next.y - previous.y) * (trackRect.height / 3800);

        setCarPosition({
            x: (current.x / 1000) * 100,
            y: (current.y / 3800) * 100,
            angle: Math.atan2(dy, dx) * (180 / Math.PI) + 90,
        });
    }, [reducedMotion, scrollProgress]);

    return (
        <div ref={trackRef} className="teams-road-track" aria-hidden>
            <svg viewBox="0 0 1000 3800" preserveAspectRatio="none">
                <path
                    ref={pathRef}
                    className="teams-road-track-edge"
                    d={TRACK_PATH}
                />
                <path
                    className="teams-road-track-outer"
                    d={TRACK_PATH}
                />
                <path
                    className="teams-road-track-inner"
                    d={TRACK_PATH}
                />
            </svg>
            <motion.div
                className="teams-scroll-car"
                style={{
                    left: `${carPosition.x}%`,
                    top: `${carPosition.y}%`,
                    rotate: reducedMotion ? 0 : carPosition.angle,
                }}
                transition={{ duration: reducedMotion ? 0 : 0.18, ease: "easeOut" }}
            >
                <div className="teams-scroll-car-body">
                    <span className="teams-scroll-car-wing teams-scroll-car-front-wing" />
                    <span className="teams-scroll-car-wing teams-scroll-car-rear-wing" />
                    <span className="teams-scroll-car-wheel teams-scroll-car-wheel-front-left" />
                    <span className="teams-scroll-car-wheel teams-scroll-car-wheel-front-right" />
                    <span className="teams-scroll-car-wheel teams-scroll-car-wheel-rear-left" />
                    <span className="teams-scroll-car-wheel teams-scroll-car-wheel-rear-right" />
                    <span className="teams-scroll-car-floor" />
                    <span className="teams-scroll-car-nose" />
                    <span className="teams-scroll-car-sidepod teams-scroll-car-sidepod-left" />
                    <span className="teams-scroll-car-sidepod teams-scroll-car-sidepod-right" />
                    <span className="teams-scroll-car-halo" />
                    <span className="teams-scroll-car-cockpit" />
                </div>
            </motion.div>
        </div>
    );
}

function RoadTeamCard({ team, side, index, onOpen }) {
    const primaryColor = team.primary_color || "#E10600";
    const isRight = side === "right";

    return (
        <motion.article
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.45, delay: Math.min(index * 0.035, 0.25), ease: [0.22, 1, 0.36, 1] }}
            className={`teams-road-card ${isRight ? "teams-road-card-right" : "teams-road-card-left"}`}
            style={{ "--team-color": primaryColor }}
        >
            <button type="button" onClick={onOpen} className="teams-road-card-button">
                <div className="teams-road-card-main">
                    <div>
                        <p>{team.visual.badge}</p>
                        <h2>{team.short_name || team.name}</h2>
                    </div>
                    <div className="teams-road-logo">
                        {team.visual.hero ? (
                            <img src={team.visual.hero} alt={`${team.name} visual`} loading="lazy" />
                        ) : (
                            <span>{team.visual.monogram}</span>
                        )}
                    </div>
                </div>
                <div className="teams-road-card-details">
                    <div className="teams-road-card-meta">
                        <span>
                            <strong>Founded</strong>
                            {team.reference.founded}
                        </span>
                        <span>
                            <strong>Base</strong>
                            {team.reference.base}
                        </span>
                    </div>
                    <div className="teams-road-card-drivers" aria-label={`${team.name} drivers`}>
                        {team.drivers.length ? (
                            team.drivers.map((driver) => (
                                <span key={driver.id}>
                                    <strong>{driver.code}</strong>
                                    {driver.name}
                                </span>
                            ))
                        ) : (
                            <span>
                                <strong>Archive</strong>
                                Historic constructor
                            </span>
                        )}
                    </div>
                </div>
                <div className="teams-road-card-footer">
                    <span>Expand</span>
                    <ArrowRight className="h-4 w-4" />
                </div>
            </button>
        </motion.article>
    );
}

function TeamGarageModal({ team, onClose }) {
    const primaryColor = team.primary_color || "#E10600";
    const stats = [
        { label: "Points", value: team.points ?? "—" },
        { label: "Position", value: `P${team.position || team.standing || "—"}` },
        { label: "Titles", value: team.championships ?? "—" },
        { label: "Wins", value: team.wins ?? "—" },
        { label: "Podiums", value: team.podiums ?? "—" },
        { label: "Driver Pts", value: team.metrics.driverPoints || "—" },
    ];

    return (
        <motion.div
            className="teams-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={onClose}
        >
            <motion.section
                className="teams-garage-modal"
                style={{ "--team-color": primaryColor }}
                initial={{ opacity: 0, scale: 0.94, y: 46, rotateX: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 28 }}
                transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                onMouseDown={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`${team.name} details`}
            >
                <button type="button" className="teams-modal-close" onClick={onClose} aria-label="Close team details">
                    <X className="h-5 w-5" />
                </button>

                <div className="teams-modal-hero">
                    {team.visual.hero ? (
                        <img src={team.visual.hero} alt={`${team.name} team visual`} />
                    ) : null}
                    <div className="teams-modal-hero-shade" />
                    <div className="teams-modal-title">
                        <p>{team.visual.signal}</p>
                        <h2>{team.short_name || team.name}</h2>
                    </div>
                </div>

                <div className="teams-modal-content">
                    <div className="teams-modal-copy">
                        <p className="label-eyebrow text-f1-red">Garage profile</p>
                        <p>{team.description || "Team profile details are ready for admin enrichment."}</p>
                        <div className="teams-modal-drivers">
                            {team.drivers.map((driver) => (
                                <div key={driver.id}>
                                    <span>{driver.code}</span>
                                    <strong>{driver.name}</strong>
                                    <small>#{driver.number || "—"} · P{driver.position || "—"}</small>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="teams-modal-stats">
                        {stats.map((stat) => (
                            <MiniStat key={stat.label} label={stat.label} value={stat.value} />
                        ))}
                    </div>

                    <div className="teams-modal-actions">
                        <Link to={`/teams/${team.id}`} className="teams-modal-link">
                            View full profile
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                        <div className="teams-modal-signal">
                            <Gauge className="h-4 w-4" />
                            <span>{team.reference.base}</span>
                        </div>
                    </div>
                </div>
            </motion.section>
        </motion.div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="teams-mini-stat">
            <p>{label}</p>
            <strong>{value}</strong>
        </div>
    );
}
